// noinspection NonAsciiCharacters
import {
  Directive,
  EmbeddedViewRef,
  Input,
  TemplateRef,
  ViewContainerRef,
  ɵstringify as stringify,
} from '@angular/core';
import { AuthorizationService, IPermission } from './authorization.service';

function assertTemplate(property: string, templateRef: TemplateRef<AppHasPermissionContext> | null): void {
  const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
  if (!isTemplateRefOrNull) {
    throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
  }
}

/**
 * @publicApi
 */
export class AppHasPermissionContext {
  public $implicit: boolean | undefined;
}

@Directive({
  selector: '[appHasPermission]',
})
export class HasPermissionDirective {
  private _context: AppHasPermissionContext = new AppHasPermissionContext();
  private _thenTemplateRef: TemplateRef<AppHasPermissionContext> | null = null;
  private _elseTemplateRef: TemplateRef<AppHasPermissionContext> | null = null;
  private _thenViewRef: EmbeddedViewRef<AppHasPermissionContext> | null = null;
  private _elseViewRef: EmbeddedViewRef<AppHasPermissionContext> | null = null;
  private _permission: keyof IPermission | undefined;

  constructor(
    private _viewContainer: ViewContainerRef,
    templateRef: TemplateRef<AppHasPermissionContext>,
    private _authorizationService: AuthorizationService,
  ) {
    this._thenTemplateRef = templateRef;
    this._updateView();
  }

  @Input()
  set appHasPermission(value: string) {
    const permissionExpression = value ? value : '';
    const not = permissionExpression.startsWith('!');
    const permission: keyof IPermission = not
      ? (permissionExpression.substr(1) as keyof IPermission)
      : (permissionExpression as keyof IPermission);
    if (this._permission !== permission) {
      this._permission = permission;
      if (!permission) {
        this._updateView();
        return;
      }
      this._authorizationService.hasPermission$(permission).subscribe((result) => {
        // for two booleans, a != b is the equivalent of a logical XOR
        this._context.$implicit = result !== not;
        this._updateView();
      });
    }
  }

  @Input()
  set appHasPermissionThen(templateRef: TemplateRef<AppHasPermissionContext> | null) {
    assertTemplate('appHasPermissionThen', templateRef);
    this._thenTemplateRef = templateRef;
    this._thenViewRef = null; // clear previous view if any.
    this._updateView();
  }

  @Input()
  set appHasPermissionElse(templateRef: TemplateRef<AppHasPermissionContext> | null) {
    assertTemplate('appHasPermissionElse', templateRef);
    this._elseTemplateRef = templateRef;
    this._elseViewRef = null; // clear previous view if any.
    this._updateView();
  }

  /**
   * Assert the correct type of the expression bound to the `appHasPermission` input within the template.
   *
   * The presence of this method is a signal to the Ivy template type check compiler that when the
   * `AppHasPermission` structural directive renders its template, the type of the expression bound to `appHasPermission`
   * should be narrowed in some way. For `appHasPermissionDirective`, it is narrowed to be non-null, which allows the
   * strictNullChecks feature of TypeScript to work with `appHasPermissionDirective`.
   */
  static ngTemplateGuard_appHasPermission<E>(dir: HasPermissionDirective, expr: E): expr is NonNullable<E> {
    return true;
  }

  private _updateView(): void {
    if (this._context.$implicit) {
      if (!this._thenViewRef) {
        this._viewContainer.clear();
        this._elseViewRef = null;
        if (this._thenTemplateRef) {
          this._thenViewRef = this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
        }
      }
    } else {
      if (!this._elseViewRef) {
        this._viewContainer.clear();
        this._thenViewRef = null;
        if (this._elseTemplateRef) {
          this._elseViewRef = this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
        }
      }
    }
  }
}
